import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Cropper from 'react-easy-crop';
import { X } from 'lucide-react';
import { cropImage } from '../../services/api';

const ImageCropModal = ({ croppingItem, onClose, onCropComplete, showNotification }) => {
  const [cropState, setCropState] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropSaving, setCropSaving] = useState(false);

  const handleCropAreaChange = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropSave = async () => {
    if (!croppingItem || !croppedAreaPixels) return;
    setCropSaving(true);
    try {
      await cropImage(croppingItem.id, {
        x: croppedAreaPixels.x,
        y: croppedAreaPixels.y,
        width: croppedAreaPixels.width,
        height: croppedAreaPixels.height
      });
      showNotification('Image cropped successfully');
      onCropComplete();
    } catch (error) {
      showNotification('Failed to crop image', 'error');
    }
    setCropSaving(false);
  };

  return (
    <AnimatePresence>
      {croppingItem && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Crop Image</h3>
                <p className="text-sm text-slate-500 truncate">{croppingItem.title}</p>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="relative w-full" style={{ height: '450px' }}>
              <Cropper
                image={croppingItem.url}
                crop={cropState}
                zoom={cropZoom}
                onCropChange={setCropState}
                onZoomChange={setCropZoom}
                onCropComplete={handleCropAreaChange}
                objectFit="contain"
              />
            </div>
            
            <div className="p-4 border-t flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>Zoom:</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={cropZoom}
                  onChange={(e) => setCropZoom(Number(e.target.value))}
                  className="w-32"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">Cancel</button>
                <button
                  onClick={handleCropSave}
                  disabled={cropSaving}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  data-testid="crop-save-btn"
                >
                  {cropSaving ? 'Saving...' : 'Save Crop'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageCropModal;
